<!-- eslint-disable vue/no-v-html -->
<script lang="ts">
import type { SetupContext } from 'vue';
import type { HekateDisplayField } from '@onshape/www-inertia';
import CoordinatorProvider from '../coordinator/CoordinatorProvider';
import FieldErrorMessage from './FieldErrorMessage.vue';

export default defineComponent({
  components: {
    FieldErrorMessage,
  },
  props: {
    field: {
      required: true,
      type: Object as unknown as () => HekateDisplayField,
    },
    modelValue: {
      required: true,
      type: String,
    },
  },
  emits: ['blur', 'focus', 'update:modelValue'],
  setup(props: { modelValue: string; field: HekateDisplayField }, context: SetupContext) {
    const { field, modelValue } = toRefs(props);
    return CoordinatorProvider.get().multiSelectField({
      field,
      modelValue,
      context,
    });
  },
});
</script>

<template>
  <div
    :class="[
      'field-multi-select',
      'field-container',
      'field-multi-select-option-list-id-' + field.optionListId,
    ]"
  >
    <div
      :class="{
        'field-content': true,
        'field-content-is-validated': field.isValidated,
        'field-content-is-errored':
          field.errorMessages.length > 0 && field.interactiveState === 'dirty',
      }"
    >
      <fieldset
        class="option-group"
        :disabled="field.isDisabled"
      >
        <legend>
          <span v-html="$t(field.name, field.htmlName, field.label)" /><sup
            v-if="field.isRequired"
            class="required-indicator"
            title="This field is required"
            >*</sup
          >
        </legend>
        <div class="option-checkboxes">
          <label
            v-for="(option, ii) in optionList"
            :key="`multi-option-${option.value}-${ii}`"
            class="option-checkbox"
          >
            <input
              :id="`${field.htmlName}-for-${field.id}-${ii}`"
              :html-name="field.htmlName"
              :value="option.value"
              class="multi-select-checkbox-input"
              type="checkbox"
              :checked="option.isSelected"
              @change="toggleOption({ value: option.value })"
              @blur="$emit('blur', field)"
              @focus="$emit('focus', field)"
            />
            <span>{{ $t(option.displayName, option.value) }}</span>
          </label>
        </div>
      </fieldset>
    </div>
    <field-error-message :messages="field.errorMessages" />
  </div>
</template>

<style lang="scss" scoped>
@use '../../../style/vars.scss' as vars;

.field-multi-select {
  width: 100%;
  margin-top: 4px;
  margin-bottom: 8px;
  border: 1px solid #eee;

  .field-content {
    display: flex;
    flex-direction: column;
    width: 100%;

    .option-group {
      min-width: 0;
      margin: 0;
      padding: 0;
      border: 0;

      > legend {
        padding: 6px 8px 4px;
      }
    }

    &.field-content-is-errored {
      .option-checkboxes {
        border-bottom: 1px solid vars.$color-error;
      }
    }
  }

  .field-content-is-validated {
    border-right: 4px solid vars.$color-onshape-green;
  }

  .option-checkboxes {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 6px 12px;
    padding: 6px 8px 8px;
  }

  .option-checkbox {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    width: 100%;
    gap: 4px;
    cursor: pointer;
    font-size: 0.7em;
    min-height: 20px;

    span {
      margin-left: 4px;
      line-height: 1.1;
    }
  }
}

.multi-select-checkbox-input {
  display: inline-block !important;
  align-self: center;
  vertical-align: middle;
  box-sizing: border-box !important;
  appearance: auto;
  -webkit-appearance: checkbox;
  margin-bottom: 0 !important;
  padding: 0 !important;
  width: 18px !important;
  height: 18px !important;
}
</style>
